import {Injectable} from '@angular/core';
import * as CryptoJS from 'crypto-js';
import {environment} from "../../../../environments/environment";

@Injectable({
    providedIn: 'root'
})
export class SecureStorageService {
    private readonly ENCRYPTION_KEY = environment.PAYLOAD_ENCRYPTION_KEY;
    private readonly SHOULD_ENCRYPT = environment.shouldEncrypt;
    private readonly MIGRATION_FLAG = '_e_v1';

    constructor() {
        // Only run migration if encryption is enabled AND not already migrated
        if (this.SHOULD_ENCRYPT && !this.isMigrated()) {
            this.migrateExistingData();
        }
    }

    // ==================== LOCAL STORAGE ====================

    /**
     * Store data in localStorage (encrypted if shouldEncrypt=true)
     * @param key - Storage key
     * @param value - Any JSON-serializable value
     */
    setItem(key: string, value: any): void {
        try {
            const stringValue = JSON.stringify(value);

            if (!this.SHOULD_ENCRYPT) {
                localStorage.setItem(key, stringValue);
                return;
            }

            const encrypted = CryptoJS.AES.encrypt(stringValue, this.ENCRYPTION_KEY).toString();
            localStorage.setItem(key, encrypted);
        } catch (err) {
            throw err;
        }
    }

    /**
     * Retrieve data from localStorage (decrypted if shouldEncrypt=true)
     * @param key - Storage key
     * @returns Parsed value or null if not found
     */
    getItem<T>(key: string): T | null {
        try {
            const value = localStorage.getItem(key);
            if (!value) return null;

            if (!this.SHOULD_ENCRYPT) {
                return JSON.parse(value);
            }

            const decrypted = CryptoJS.AES.decrypt(value, this.ENCRYPTION_KEY);
            const text = decrypted.toString(CryptoJS.enc.Utf8);

            if (!text) {
                console.warn(` Key "${key}" exists but decryption returned empty string`);
                return null;
            }

            return JSON.parse(text);
        } catch (err) {
            return null;
        }
    }

    /**
     * Remove item from localStorage
     */
    removeItem(key: string): void {
        localStorage.removeItem(key);
    }

    /**
     * Clear all localStorage
     */
    clear(): void {
        localStorage.clear();
    }

    // ==================== SESSION STORAGE ====================

    /**
     * Store data in sessionStorage (encrypted if shouldEncrypt=true)
     * @param key - Storage key
     * @param value - Any JSON-serializable value
     */
    setSessionItem(key: string, value: any): void {
        try {
            const stringValue = JSON.stringify(value);

            if (!this.SHOULD_ENCRYPT) {
                sessionStorage.setItem(key, stringValue);
                return;
            }

            const encrypted = CryptoJS.AES.encrypt(stringValue, this.ENCRYPTION_KEY).toString();
            sessionStorage.setItem(key, encrypted);
        } catch (err) {
            console.error(` Failed to save session key "${key}":`, err);
            throw err;
        }
    }

    /**
     * Retrieve data from sessionStorage (decrypted if shouldEncrypt=true)
     * @param key - Storage key
     * @returns Parsed value or null if not found
     */
    getSessionItem<T>(key: string): T | null {
        try {
            const value = sessionStorage.getItem(key);
            if (!value) return null;

            if (!this.SHOULD_ENCRYPT) {
                return JSON.parse(value);
            }

            const decrypted = CryptoJS.AES.decrypt(value, this.ENCRYPTION_KEY);
            const text = decrypted.toString(CryptoJS.enc.Utf8);

            if (!text) {
                console.warn(` Session key "${key}" exists but decryption returned empty string`);
                return null;
            }

            return JSON.parse(text);
        } catch (err) {
            console.error(`Failed to read session key "${key}":`, err);
            return null;
        }
    }

    /**
     * Remove item from sessionStorage
     */
    removeSessionItem(key: string): void {
        sessionStorage.removeItem(key);
    }

    /**
     * Clear all sessionStorage
     */
    clearSession(): void {
        sessionStorage.clear();
    }

    // ==================== MIGRATION LOGIC ====================

    private isMigrated(): boolean {
        return localStorage.getItem(this.MIGRATION_FLAG) === 'true';
    }

    private migrateExistingData(): void {
        console.log(' [Migration] Starting localStorage encryption...');

        try {
            const snapshot: Array<{ key: string, value: string }> = [];

            // Step 1: Snapshot all existing data
            for (let i = 0; i < localStorage.length; i++) {
                const key = localStorage.key(i);
                if (!key || key === this.MIGRATION_FLAG) continue;

                const value = localStorage.getItem(key);
                if (value) {
                    snapshot.push({key, value});
                }
            }

            if (snapshot.length === 0) {
                console.log(' [Migration] No data to migrate');
                localStorage.setItem(this.MIGRATION_FLAG, 'true');
                return;
            }

            // Step 2: Encrypt and overwrite each item
            let successCount = 0;
            for (const item of snapshot) {
                try {
                    // Try to parse - if it parses, it's probably plaintext JSON
                    JSON.parse(item.value);

                    // It parsed successfully, so it's plaintext - encrypt it
                    const encrypted = CryptoJS.AES.encrypt(item.value, this.ENCRYPTION_KEY).toString();
                    localStorage.setItem(item.key, encrypted);
                    successCount++;
                } catch {
                    // Parsing failed - might already be encrypted or corrupted
                    // Leave it as-is to avoid data loss
                    console.warn(`️ [Migration] Skipping key "${item.key}" - not valid JSON`);
                }
            }

            // Step 3: Mark migration as complete
            localStorage.setItem(this.MIGRATION_FLAG, 'true');
            console.log(` [Migration] Completed - encrypted ${successCount}/${snapshot.length} keys`);
        } catch (err) {
            console.error(' [Migration] Failed:', err);
            // Don't set migration flag on failure - will retry next time
        }
    }

    /**
     * Force re-migration (useful for testing or debugging)
     * ️ Use with caution - only for development
     */
    forceMigration(): void {
        localStorage.removeItem(this.MIGRATION_FLAG);
        this.migrateExistingData();
    }
}